<template>
  <div id="app">
    <Header :header="settingLayout.header" />
    <UserBanner v-if="$route.meta.isHeader" :menu="menu" />
    <div class="user-cabinet">
      <div class="container">
        <template v-if="$route.meta.isMenu">
          <UserCabinetSubmenu />
          <SubmenuBar
            v-if="submenuLeft.length || submenuRight.length"
            :left="submenuLeft"
            :right="submenuRight"
          />
        </template>
        <router-view />
      </div>
    </div>
    <Cookie v-if="!isAccepedCookie" @onAcceptCookie="acceptCookie"/>
    <Footer />
  </div>
</template>
<script>
import Footer from '@/components/layout/Footer.vue';
import Header from '@/components/layout/Header.vue';
import Cookie from '@/components/layout/Cookie.vue';
import UserBanner from '@/components/profile/UserBanner.vue';
import UserCabinetSubmenu from '@/components/profile/UserCabinetSubmenu.vue';
import SubmenuBar from '@/components/profile/SubmenuBar.vue';
import menu from '@/mixins/menu/profile';
import content from '@/mixins/content';
import cookie from '@/mixins/cookie';
import websocket from '@/mixins/websocket';
import me from '@/mixins/me';

export default {
  name: 'Profile',
  mixins: [menu, content, cookie, websocket, me],
  components: {
    Header,
    Cookie,
    Footer,
    UserBanner,
    UserCabinetSubmenu,
    SubmenuBar,
  },
  props: {
    settingLayout: {
      type: Object,
    },
  },
  data() {
    return {};
  },
  created() {
    this.subscribeToChannel('marketplace', this.setUnreadMarketplaceFromSocket);
    this.subscribeToChannel('profile-feed', this.setUnreadFeedsFromSocket);
    this.subscribeToChannel('profile-projects', this.setUnreadProjectsFromSocket);
    this.subscribeToChannel('profile-groups', this.setUnreadGroupsFromSocket);
    this.subscribeToChannel('profile-friends', this.setUnreadFriendsFromSocket);
  },
  methods: {
    setUnreadMarketplaceFromSocket(event) {
      let data = {};
      try {
        data = JSON.parse(event.data) || {};
      } catch (e) {
        return;
      }
      this.$store.commit('SET_UNREAD_MARKETPLACE_DATA', data);
    },
    setUnreadFeedsFromSocket(event) {
      const data = JSON.parse(event.data).message;
      const newFeed = JSON.parse(event.data).update;
      this.$store.commit(
        'SET_UNREAD_FEEDS',
        data.feed,
      );

      if (this.$route.name === 'profileFeed') {
        this.$eventBus.$emit('setFeedsCount', 0, 'feeds');
      }

      if (newFeed) {
        this.$eventBus.$emit('addedProfileFeed', newFeed);
      }
    },
    setUnreadProjectsFromSocket(event) {
      const data = JSON.parse(event.data).message;
      this.$store.commit('SET_UNREAD_PROJECTS', data.projects);

      this.$eventBus.$emit('setGroupsCount', data.groups, 'groups');
      this.$eventBus.$emit('setFeedsCount', data.feed, 'feeds');
      this.$eventBus.$emit('setFriendsCount', data.friends, 'friends');
    },
    setUnreadGroupsFromSocket(event) {
      const data = JSON.parse(event.data).message;
      this.$store.commit('SET_UNREAD_GROUPS', data.groups);

      this.$eventBus.$emit('setProjectsCount', data.projects, 'projects');
    },
    setUnreadFriendsFromSocket(event) {
      const data = JSON.parse(event.data).message;
      const newFriend = JSON.parse(event.data).update;

      this.$store.commit(
        'SET_UNREAD_MEMBERS',
        data.friends,
      );

      if (this.$route.name === 'profileFriends') {
        this.$eventBus.$emit('setFriendsCount', 0, 'friends');
      }

      if (newFriend) {
        this.$eventBus.$emit('addedProfileFriend', newFriend);
      }
    },
  },
  computed: {
    isBuyer() {
      return this.$route.params.type === 'buyer';
    },
    isMatchmakingRoute() {
      return this.$route.path.includes('/profile/matchmaking');
    },
    isGroups() {
      return this.$route.path.startsWith('/profile/groups') || this.$route.name === 'profileGroupsRequest';
    },
    matchmakingType() {
      return this.$route.path.includes('/profile/matchmaking/seller') ? 'seller' : 'buyer';
    },
    marketplaceUnreadTotal() {
      const buyer = Number(this.$store.getters.getUnreadBuyerMarketplace) || 0;
      const seller = Number(this.$store.getters.getUnreadSellerMarketplace) || 0;
      return buyer + seller;
    },
    buyerProductsUnreadCount() {
      return Number(this.$store.getters.marketplaceProductsTotal('buyer')) || 0;
    },
    submenuLeft() {
      if (this.isMatchmakingRoute) {
        if (this.matchmakingType === 'seller') {
          const isEnterNewProduct = String(this.$route.query.create || '') === '1';
          const isSavedProjects = this.$route.name === 'profileMatchmakingSellerSaved';

          return [
            {
              key: 'seller-new-product',
              title: 'Enter new product',
              active: this.$route.name === 'profileMatchmakingSellerProducts' && isEnterNewProduct,
              to: {
                name: 'profileMatchmakingSellerProducts',
                query: { ...this.$route.query, create: '1' },
              },
            },
            {
              key: 'seller-my-products',
              title: 'My products',
              active: this.$route.name === 'profileMatchmakingSellerProducts'
                && !isEnterNewProduct
                && !isSavedProjects,
              to: { name: 'profileMatchmakingSellerProducts' },
            },
            {
              key: 'seller-my-subscriptions',
              title: 'My subscriptions',
              active: this.$route.name === 'profileMatchmakingSellerSubscriptions',
              to: { name: 'profileMatchmakingSellerSubscriptions' },
            },
          ];
        }
        const view = String(this.$route.query.view || '');
        const isRfqView = !view || view === 'rfqs';
        const isCreateRfq = String(this.$route.query.create || '') === '1';

        return [
          {
            key: 'buyer-create',
            title: 'Create new RFQ',
            active: isRfqView && isCreateRfq,
            to: { name: 'profileMatchmaking', params: { type: 'buyer' }, query: { create: '1' } },
          },
          {
            key: 'buyer-my',
            title: "My RFQ's",
            active: isRfqView
              && !isCreateRfq
              && (this.$route.name === 'profileMatchmaking' || this.$route.name === 'projectDetails'),
            to: { name: 'profileMatchmaking', params: { type: 'buyer' } },
          },
        ];
      }

      return [];
    },

    submenuRight() {
      if (this.isMatchmakingRoute) {
        if (this.matchmakingType === 'seller') {
          return [
            {
              key: 'seller-find-projects',
              title: 'Find projects',
              active: this.$route.name === 'profileMatchmakingSellerProjects' || this.$route.name === 'projectDetails',
              to: { name: 'profileMatchmakingSellerProjects' },
            },
            {
              key: 'seller-saved-projects',
              title: 'Saved projects',
              active: this.$route.name === 'profileMatchmakingSellerSaved',
              to: { name: 'profileMatchmakingSellerSaved' },
            },
          ];
        }
        const view = String(this.$route.query.view || '');

        return [
          {
            key: 'buyer-find-products',
            title: 'Find products',
            active: view === 'products',
            count: this.buyerProductsUnreadCount,
            to: { name: 'profileMatchmaking', params: { type: 'buyer' }, query: { view: 'products' } },
          },
          {
            key: 'buyer-saved-products',
            title: 'Saved products',
            active: view === 'saved-products',
            to: {
              name: 'profileMatchmaking',
              params: { type: 'buyer' },
              query: { view: 'saved-products' },
            },
          },
        ];
      }

      if (this.isGroups) {
        return [];
      }

      return [
        {
          key: 'help',
          title: 'Help',
          active: false,
          to: { name: 'ContactPage' },
        },
        {
          key: 'settings',
          title: 'Settings',
          active: false,
          to: { name: 'profileSetting' },
        },
      ];
    },
  },
  watch: {
    marketplaceUnreadTotal: {
      immediate: true,
      handler(val) {
        this.$eventBus.$emit('setMatchmaking', Number(val) || 0, 'matchmaking');
      },
    },
    '$route.path': {
      immediate: true,
      handler(newPath) {
        if (!newPath || !newPath.includes('/profile/matchmaking')) return;

        const isBuyerRoute = !newPath.includes('/profile/matchmaking/seller');
        if (this.$store.getters.isBuyer !== isBuyerRoute) {
          this.$store.commit('SET_IS_BUYER', isBuyerRoute);
        }
      },
    },
    /* eslint-disable */
    '$route.name': function (newVal) {
      if (newVal === 'profileMatchmaking' || newVal === 'profileMatchmakingSellerProjects') {
        this.$eventBus.$emit('setMatchmaking', this.marketplaceUnreadTotal, 'matchmaking');
      }
    },
  },
};
</script>

<style lang="scss">
.user-cabinet {
  padding-bottom: 0 !important;
}
</style>
